
import React, { useState, useEffect } from "react";
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { ProfileForm } from "./onboarding/ProfileForm";
import { useProfileSubmission } from "./onboarding/useProfileSubmission";
import { ScrollArea } from "@/components/ui/scroll-area";

interface UserOnboardingProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onComplete: (userData: {
    displayName: string;
    bio: string;
    knittingStyle: "continental" | "english" | "not-specified";
    measurementSystem: "metric" | "imperial";
    avatarUrl: string;
    emailMarketing: boolean;
    emailProjectUpdates: boolean;
    emailCommunityUpdates: boolean;
  }) => void;
  userId?: string | null;
  initialDisplayName?: string;
}

export const UserOnboarding = ({ 
  open, 
  onOpenChange, 
  onComplete, 
  userId, 
  initialDisplayName = "" 
}: UserOnboardingProps) => {
  const [displayName, setDisplayName] = useState(initialDisplayName);
  const [bio, setBio] = useState("");
  const [knittingStyle, setKnittingStyle] = useState<"continental" | "english" | "not-specified">("not-specified");
  const [measurementSystem, setMeasurementSystem] = useState<"metric" | "imperial">("metric");
  const [customAvatar, setCustomAvatar] = useState<string | null>(null);
  const [communicationPrefs, setCommunicationPrefs] = useState({
    email_marketing: true,
    email_project_updates: true,
    email_community_updates: true
  });
  
  // Update displayName when initialDisplayName changes
  useEffect(() => {
    if (initialDisplayName) {
      setDisplayName(initialDisplayName);
    }
  }, [initialDisplayName]);
  
  const { handleSubmit, skipSetup, loading } = useProfileSubmission(onComplete, onOpenChange, userId);
  
  const handlePreferenceChange = (key: string, checked: boolean) => {
    setCommunicationPrefs(prev => ({
      ...prev,
      [key]: checked
    }));
  };
  
  const onSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    
    // Default to initials for avatar URL if no custom avatar
    let avatarUrl = "";
    if (customAvatar) {
      avatarUrl = customAvatar;
    }
    
    handleSubmit({
      displayName,
      bio,
      knittingStyle,
      measurementSystem,
      avatarUrl,
      communicationPrefs
    });
  };
  
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-[500px] p-0 max-h-[90vh] overflow-hidden">
        <ScrollArea className="max-h-[90vh]">
          <div className="p-6">
            <form onSubmit={onSubmit}>
              <DialogHeader>
                <DialogTitle className="text-2xl text-center">Complete your profile</DialogTitle>
                <DialogDescription className="text-center">
                  Let's set up your profile so you can get the most out of our knitting community
                </DialogDescription>
              </DialogHeader>
              
              <ProfileForm 
                displayName={displayName}
                setDisplayName={setDisplayName}
                bio={bio}
                setBio={setBio}
                knittingStyle={knittingStyle}
                setKnittingStyle={setKnittingStyle}
                measurementSystem={measurementSystem}
                setMeasurementSystem={setMeasurementSystem}
                customAvatar={customAvatar}
                setCustomAvatar={setCustomAvatar}
                communicationPrefs={communicationPrefs}
                handlePreferenceChange={handlePreferenceChange}
              />
              
              <DialogFooter className="flex flex-col sm:flex-row gap-2 mt-6">
                <Button type="button" variant="outline" onClick={skipSetup} disabled={loading}>
                  Skip for now
                </Button>
                <Button type="submit" disabled={loading}>
                  {loading ? "Saving..." : "Save Profile"}
                </Button>
              </DialogFooter>
            </form>
          </div>
        </ScrollArea>
      </DialogContent>
    </Dialog>
  );
};
